<!--
	Alert: status message with an icon. `page` centres it as the main content of a failed view;
	`inline` sits in the flow. Danger and warning interrupt (role="alert") unless `announce` is off.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import IconExclamationCircle from '#lib/assets/exclamation-circle.svg?raw';

	interface Props {
		variant?: 'info' | 'warning' | 'danger';
		layout?: 'inline' | 'page';
		/** Bold lead line. */
		message?: string;
		/** Off for alerts that render with their content rather than in response to an action. */
		announce?: boolean;
		children?: Snippet;
	}

	const {
		variant = 'danger',
		layout = 'inline',
		message,
		announce = true,
		children
	}: Props = $props();

	const role = $derived(
		!announce ? undefined : variant === 'danger' || variant === 'warning' ? 'alert' : 'status'
	);
</script>

<div class="alert {variant} {layout}" {role}>
	<span class="icon" aria-hidden="true">{@html IconExclamationCircle}</span>
	<div class="content">
		{#if message}<p class="message">{message}</p>{/if}
		{@render children?.()}
	</div>
</div>

<style>
	.alert {
		--alert-ink: var(--status-danger);
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
		border-inline-start: 4px solid var(--alert-ink);
		border-radius: var(--radius);
		background-color: var(--status-danger-surface);
		padding: var(--space-2) var(--space-4);
		color: var(--text-color);
		font-size: var(--font-size-sm);

		&.info {
			--alert-ink: var(--status-info);
			background-color: var(--status-info-surface);
		}

		&.warning {
			--alert-ink: var(--status-warning);
			background-color: var(--status-warning-surface);
		}

		&.page {
			margin: 15vh auto auto;
			padding: var(--space-5) var(--space-6);
			width: min(var(--width-lg), 100% - var(--space-6));
			font-size: var(--font-size-base);

			.message {
				font-size: var(--font-size-xl);
			}
		}

		.icon {
			flex-shrink: 0;
			margin-top: 0.1em;
			width: var(--icon-lg);
			height: var(--icon-lg);
			color: var(--alert-ink);

			:global(svg) {
				stroke: currentColor;
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			gap: var(--space-1);
			min-width: 0;
			overflow-wrap: break-word;

			:global(a) {
				color: inherit;
			}
		}

		.message {
			font-weight: var(--weight-semibold);
		}
	}
</style>
